import * as React from "react";
import { inject, observer } from "mobx-react";
import moment from "moment";
import { MobxComponent } from "../../../../../mobx/components";
import { Button, Tooltip } from "@lib/components";
import { UI } from "../../../../../core/ui";

/** What GET .../pos/status returns. `enabled` is false when the deployment does not dispatch. */
export interface POSSyncStatus {
	enabled: boolean;
	pending_since?: number;
	last_sync?: { at: number; ok: boolean; rows: number; error?: string } | null;
	/** True while a sync is under way. Absent from a menu-service older than the field. */
	syncing?: boolean;
}

const POLL_EVERY_MS = 2000;
const GIVE_UP_AFTER_MS = 60000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** When the last result was recorded, by the server's clock; 0 before the first. */
export const lastSyncAt = (status: POSSyncStatus | null) =>
	status && status.last_sync ? status.last_sync.at : 0;

/**
 * Waits for a sync's outcome and tells the operator how it went.
 *
 * Done when the status carries a result newer than `since`, both server times, so the browser's
 * clock plays no part. `syncing` turning false with no newer result means no sync ran, which is
 * what a restaurant with no PushPOS key gets. A minute is the backstop. `onStatus` sees every
 * read; `cancelled` stops the wait once the screen is gone.
 */
export async function waitForPOSSync(
	store: any,
	restaurantID: string,
	since: number,
	onStatus: (status: POSSyncStatus | null) => void,
	cancelled: () => boolean,
): Promise<void> {
	const started = Date.now();
	while (Date.now() - started < GIVE_UP_AFTER_MS) {
		await wait(POLL_EVERY_MS);
		if (cancelled()) {
			return;
		}
		const status: POSSyncStatus | null = await store.syncStatusWPOS(restaurantID);
		onStatus(status);
		if (!status) {
			continue;
		}
		const last = status.last_sync;
		if (last && last.at > since) {
			if (last.ok) {
				UI.notification.success("Menus synced to the POS");
			} else {
				UI.notification.error(`POS sync failed${last.error ? `: ${last.error}` : ""}`);
			}
			return;
		}
		// Strictly false: an older menu-service does not send the field, and must not read as
		// "nothing ran".
		if (status.syncing === false) {
			UI.notification.warning("No sync ran. Check the PushPOS settings");
			return;
		}
	}
	UI.notification.warning("The POS sync is taking longer than usual. Refresh the page later to see how it went");
}

/** Requests a sync, then waits for its outcome. */
export async function requestPOSSync(
	store: any,
	restaurantID: string,
	onStatus: (status: POSSyncStatus | null) => void,
	cancelled: () => boolean,
): Promise<void> {
	const before: POSSyncStatus | null = await store.syncStatusWPOS(restaurantID);
	// Unreadable, so there is no result to compare against: only one recorded from now on counts.
	const since = before ? lastSyncAt(before) : Date.now();

	try {
		await store.syncMenuWPOS(restaurantID);
	} catch (e) {
		UI.notification.error("Could not request a sync");
		return;
	}
	await waitForPOSSync(store, restaurantID, since, onStatus, cancelled);
}

interface State {
	syncing: boolean;
	status: POSSyncStatus | null;
}

/**
 * Sends every menu marked for the POS.
 *
 * It lives on the menus screens rather than in the PushPOS integration settings: settings
 * hold configuration, and this is where someone has just changed a menu they want pushed.
 *
 * Available whether or not the restaurant is on manual sync, because it is also how you
 * retry after a failure. Without it the only lever was toggling a menu's "Menu
 * Synchronisation" off and on, which deletes that menu from the POS on the way past.
 */
@inject("store") @observer
export class MenuPOSSync extends MobxComponent<{}, State> {
	constructor(props: {}) {
		super(props);
		this.state = { syncing: false, status: null };
	}

	unmounted = false;

	componentDidMount() {
		this.loadStatus();
	}

	componentWillUnmount() {
		this.unmounted = true;
	}

	enabled = () => {
		const r = this.injected.store.restaurant;
		return !!(r && r.wpos && r.wpos.wpos_status);
	};

	loadStatus = async () => {
		if (!this.enabled()) {
			return;
		}
		const r = this.injected.store.restaurant!;
		const status: POSSyncStatus | null = await this.injected.store.syncStatusWPOS(r._id);
		this.setState({ status: status || null });

		// A sync already under way when the screen opened: a reload mid-sync, or an automatic one.
		if (status && status.syncing && !this.state.syncing) {
			this.setState({ syncing: true });
			await waitForPOSSync(
				this.injected.store,
				r._id,
				lastSyncAt(status),
				(s) => this.setState({ status: s }),
				() => this.unmounted,
			);
			if (!this.unmounted) {
				this.setState({ syncing: false });
			}
		}
	};

	syncNow = async () => {
		if (this.state.syncing) {
			return;
		}
		const r = this.injected.store.restaurant!;
		this.setState({ syncing: true });
		await requestPOSSync(
			this.injected.store,
			r._id,
			(status) => this.setState({ status }),
			() => this.unmounted,
		);
		if (!this.unmounted) {
			this.setState({ syncing: false });
		}
	};

	// A failure outranks pending: it is the only state the operator has to act on.
	// Empty before the first sync — there is nothing to report, and saying so is noise.
	tooltip = () => {
		const s = this.state.status;
		if (!s || !s.last_sync) {
			return "";
		}
		if (!s.last_sync.ok) {
			return `Last sync failed${s.last_sync.error ? `: ${s.last_sync.error}` : ""}`;
		}
		if (s.pending_since) {
			return `Changes not yet sent · last synced ${moment(s.last_sync.at).fromNow()}`;
		}
		return `Last synced ${moment(s.last_sync.at).fromNow()}`;
	};

	label = () => {
		if (this.state.syncing) {
			return "Syncing…";
		}
		const s = this.state.status;
		if (s && s.last_sync && !s.last_sync.ok) {
			return "Sync to POS — failed";
		}
		if (s && s.pending_since) {
			return "Sync to POS — pending";
		}
		return "Sync to POS";
	};

	render() {
		if (!this.enabled()) {
			return null;
		}
		const button = (
			<Button
				size="xxs"
				color="white"
				disabled={this.state.syncing}
				onClick={this.syncNow}
			>
				{this.label()}
			</Button>
		);
		const tooltip = this.tooltip();

		return (
			<div className="inline-block">
				{tooltip ? (
					<Tooltip text={tooltip} width={240} position="top">
						{button}
					</Tooltip>
				) : (
					button
				)}
			</div>
		);
	}
}
